import React, { useEffect, useState, useRef } from "react";
// import "bootstrap/dist/css/bootstrap.min.css";
// Remove this line — Chart.js is now loaded globally via <script>
// import Chart from "chart.js/auto";

function MarketMoodAnalyzer() {
    const apiBase = "https://osr-pr2.p-a-systems.com/api/v1/moods/marketmood";

    const [sources, setSources] = useState([]);
    const [source, setSource] = useState("fastmarket", "bdsv");
    const [topicWeekly, setTopicWeekly] = useState([]);
    const [roleScores, setRoleScores] = useState(null);
    const [roleDrivers, setRoleDrivers] = useState(null);
    const [weeklySummary, setWeeklySummary] = useState(null);
    const [llmSummaries, setLlmSummaries] = useState(null);
    const [dailyData, setDailyData] = useState([]);
    const [downloadUrl, setDownloadUrl] = useState("#");
    const [period, setPeriod] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const mmiChartRef = useRef(null);
    const mmiChartInstance = useRef(null);
    const sentimentChartRef = useRef(null);
    const sentimentChartInstance = useRef(null);
    const topicVolumeChartRef = useRef(null);
    const topicVolumeChartInstance = useRef(null);
    const topicPolarityChartRef = useRef(null);
    const topicPolarityChartInstance = useRef(null);
    const roleScoresChartRef = useRef(null);
    const roleScoresChartInstance = useRef(null);

    useEffect(() => {
        async function fetchSources() {
            try {
                setSources(["fastmarket", "bdsv"]);
            } catch {
                setSources(["fastmarket", "bdsv"]);
            }
        }
        fetchSources();
    }, []);

    useEffect(() => {
        async function loadData() {
            if (!source) return;

            setLoading(true);
            setError(null);

            try {
                const res = await fetch(`${apiBase}/${source}/analysis/latest`);
                if (!res.ok) throw new Error("Failed to load analysis data");

                const data = await res.json();

                if (data.topic_weekly_extended?.length) setTopicWeekly(data.topic_weekly_extended);
                else setTopicWeekly([]);

                setRoleScores(data.role_weekly_scores || null);
                setRoleDrivers(data.role_drivers || null);
                setWeeklySummary(data.weekly_summary || null);
                setLlmSummaries(data.llm_summaries || null);
                setDailyData(data.daily || []);

                if (data.period?.start_date && data.period?.end_date)
                    setPeriod(`${data.period.start_date} → ${data.period.end_date}`);
                else setPeriod("");

                setDownloadUrl(`${apiBase}/${source}/report/latest`);

                // Use the global Chart.js (window.Chart)
                if (window.Chart && data.daily?.length) {
                    setTimeout(() => {
                        createCharts(data);
                    }, 150);
                }


            } catch (err) {
                console.error(err);
                setError("No valid report found for this source.");
                setTopicWeekly([]);
                setRoleScores(null);
                setRoleDrivers(null);
                setWeeklySummary(null);
                setLlmSummaries(null);
                setDailyData([]);
            } finally {
                setLoading(false);
            }
        }

        loadData();

        // optional cleanup to prevent overlapping charts
        return () => {
            if (mmiChartInstance.current) mmiChartInstance.current.destroy();
            if (sentimentChartInstance.current) sentimentChartInstance.current.destroy();
            if (topicVolumeChartInstance.current) topicVolumeChartInstance.current.destroy();
            if (topicPolarityChartInstance.current) topicPolarityChartInstance.current.destroy();
            if (roleScoresChartInstance.current) roleScoresChartInstance.current.destroy();
        };
    }, [source]);

    const handleDownload = async () => {
        try {
            const response = await fetch(downloadUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${source}_report_${period.replace(/\s+→\s+/g, '_').replace(/\s+/g, '')}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Download failed:', err);
            // Fallback to opening in new tab
            window.open(downloadUrl, '_blank');
        }
    };

    const createCharts = (data) => {
        // 1. MMI Trend Line Chart
        if (mmiChartRef.current) {
            const ctx = mmiChartRef.current.getContext("2d");
            if (mmiChartInstance.current) mmiChartInstance.current.destroy();

            mmiChartInstance.current = new window.Chart(ctx, {
                type: "line",
                data: {
                    labels: data.daily.map(d => d.date.split(' ')[0]),
                    datasets: [{
                        label: "MMI",
                        data: data.daily.map(d => d.mmi),
                        borderColor: "#0d6efd",
                        backgroundColor: "rgba(13, 110, 253, 0.1)",
                        fill: true,
                        tension: 0.4
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        title: { display: false }
                    },
                    scales: {
                        y: {
                            title: { display: true, text: "MMI Score" },
                            grid: { color: "#e9ecef" }
                        },
                        x: {
                            grid: { display: false }
                        }
                    }
                }
            });
        }

        // 2. Daily Sentiment Breakdown (Stacked Bar)
        if (sentimentChartRef.current) {
            const ctx = sentimentChartRef.current.getContext("2d");
            if (sentimentChartInstance.current) sentimentChartInstance.current.destroy();

            const total = data.daily.map(d => d.pos + d.neg + d.neu);

            sentimentChartInstance.current = new window.Chart(ctx, {
                type: "bar",
                data: {
                    labels: data.daily.map(d => d.date.split(' ')[0]),
                    datasets: [
                        {
                            label: "Positive",
                            data: data.daily.map((d, i) => (d.pos / total[i] * 100).toFixed(1)),
                            backgroundColor: "#198754",
                        },
                        {
                            label: "Neutral",
                            data: data.daily.map((d, i) => (d.neu / total[i] * 100).toFixed(1)),
                            backgroundColor: "#6c757d",
                        },
                        {
                            label: "Negative",
                            data: data.daily.map((d, i) => (d.neg / total[i] * 100).toFixed(1)),
                            backgroundColor: "#dc3545",
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { position: 'bottom' }
                    },
                    scales: {
                        x: { stacked: true, grid: { display: false } },
                        y: {
                            stacked: true,
                            title: { display: true, text: "Percentage (%)" },
                            max: 100,
                            grid: { color: "#e9ecef" }
                        }
                    }
                }
            });
        }

        // 3. Topic Volume Bar Chart
        if (topicVolumeChartRef.current && data.topic_weekly_extended) {
            const ctx = topicVolumeChartRef.current.getContext("2d");
            if (topicVolumeChartInstance.current) topicVolumeChartInstance.current.destroy();

            const sortedTopics = [...data.topic_weekly_extended].sort((a, b) => b.volume - a.volume);

            topicVolumeChartInstance.current = new window.Chart(ctx, {
                type: "bar",
                data: {
                    labels: sortedTopics.map(t => t.topic),
                    datasets: [{
                        label: "Volume",
                        data: sortedTopics.map(t => t.volume),
                        backgroundColor: "#0dcaf0"
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    plugins: {
                        legend: { display: false }
                    },
                    scales: {
                        x: {
                            title: { display: true, text: "Sentence Count" },
                            grid: { color: "#e9ecef" }
                        },
                        y: {
                            grid: { display: false }
                        }
                    }
                }
            });
        }

        // 4. Topic Polarity Horizontal Bar
        if (topicPolarityChartRef.current && data.topic_weekly_extended) {
            const ctx = topicPolarityChartRef.current.getContext("2d");
            if (topicPolarityChartInstance.current) topicPolarityChartInstance.current.destroy();

            const sortedTopics = [...data.topic_weekly_extended].sort((a, b) => a.topic_weekly_score - b.topic_weekly_score);

            topicPolarityChartInstance.current = new window.Chart(ctx, {
                type: "bar",
                data: {
                    labels: sortedTopics.map(t => t.topic),
                    datasets: [{
                        label: "Topic Score",
                        data: sortedTopics.map(t => t.topic_weekly_score),
                        backgroundColor: sortedTopics.map(t =>
                            t.topic_weekly_score >= 0 ? "#198754" : "#dc3545"
                        )
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    plugins: {
                        legend: { display: false }
                    },
                    scales: {
                        x: {
                            title: { display: true, text: "Sentiment Score" },
                            grid: { color: "#e9ecef" }
                        },
                        y: {
                            grid: { display: false }
                        }
                    }
                }
            });
        }

        // 5. Role Scores Bar Chart
        if (roleScoresChartRef.current && data.role_weekly_scores) {
            const ctx = roleScoresChartRef.current.getContext("2d");
            if (roleScoresChartInstance.current) roleScoresChartInstance.current.destroy();

            const roles = Object.keys(data.role_weekly_scores);
            const scores = Object.values(data.role_weekly_scores);

            roleScoresChartInstance.current = new window.Chart(ctx, {
                type: "bar",
                data: {
                    labels: roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)),
                    datasets: [{
                        label: "Role Score",
                        data: scores,
                        backgroundColor: scores.map(s => s >= 0 ? "#198754" : "#dc3545")
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false }
                    },
                    scales: {
                        y: {
                            title: { display: true, text: "Sentiment Score" },
                            grid: { color: "#e9ecef" }
                        },
                        x: {
                            grid: { display: false }
                        }
                    }
                }
            });
        }
    };

    return (
        <div className="container-fluid p-4" style={{ maxWidth: "1400px" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-1">Market Mood Analyzer</h2>
                    {period && <p className="text-muted mb-0">Period: <strong>{period}</strong></p>}
                </div>
                <div style={{ minWidth: "250px" }}>
                    <select
                        className="form-select"
                        value={source}
                        onChange={e => setSource(e.target.value)}
                    >
                        {sources.map((s, idx) => (
                            <option key={idx} value={s}>
                                {s.toUpperCase()}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading && <div className="text-center p-5"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>}
            {error && <div className="alert alert-danger">{error}</div>}

            {!loading && !error && (
                <>
                    {/* Executive Summary */}
                    {weeklySummary && (
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">Executive Summary</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-bold">Average MMI</td>
                                                    <td className={weeklySummary.average_mmi >= 0 ? 'text-success' : 'text-danger'}>
                                                        {weeklySummary.average_mmi?.toFixed(4)}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold">Best Day</td>
                                                    <td>{weeklySummary.strongest_day}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold">Worst Day</td>
                                                    <td>{weeklySummary.weakest_day}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-bold">Volatility</td>
                                                    <td>
                                                        {weeklySummary.volatility?.toFixed(4)}
                                                        <span className="badge bg-warning text-dark ms-2">{weeklySummary.volatility_label}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold">Positive %</td>
                                                    <td className="text-success">{weeklySummary.pos_pct?.toFixed(1)}%</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold">Negative %</td>
                                                    <td className="text-danger">{weeklySummary.neg_pct?.toFixed(1)}%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Charts Row 1: MMI Trend */}
                    <div className="card mb-4 shadow-sm">
                        <div className="card-header">
                            <h6 className="mb-0">MMI Trend</h6>
                        </div>
                        <div className="card-body">
                            <p className="text-muted small mb-3">
                                Line chart showing the daily Market Mood Index (MMI) over the selected period.
                            </p>
                            <div style={{ height: "250px" }}>
                                <canvas ref={mmiChartRef}></canvas>
                            </div>
                        </div>
                    </div>

                    {/* Charts Row 2: Daily Sentiment Breakdown */}
                    <div className="card mb-4 shadow-sm">
                        <div className="card-header">
                            <h6 className="mb-0">Daily Sentiment Breakdown</h6>
                        </div>
                        <div className="card-body">
                            <p className="text-muted small mb-3">
                                Stacked bar chart displaying the percentage of positive, neutral, and negative sentiment per day.
                            </p>
                            <div style={{ height: "250px" }}>
                                <canvas ref={sentimentChartRef}></canvas>
                            </div>
                        </div>
                    </div>

                    {/* Charts Row 3: Two columns */}
                    <div className="row mb-4">
                        <div className="col-lg-6">
                            <div className="card shadow-sm h-100">
                                <div className="card-header">
                                    <h6 className="mb-0">Topic Volume</h6>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted small mb-3">
                                        Bar chart showing the total number of sentences assigned to each topic.
                                    </p>
                                    <div style={{ height: "300px" }}>
                                        <canvas ref={topicVolumeChartRef}></canvas>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="card shadow-sm h-100">
                                <div className="card-header">
                                    <h6 className="mb-0">Topic Polarity</h6>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted small mb-3">
                                        Horizontal bar chart illustrating the average weekly sentiment score for each topic.
                                    </p>
                                    <div style={{ height: "300px" }}>
                                        <canvas ref={topicPolarityChartRef}></canvas>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Role Scores Chart */}
                    {roleScores && (
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header">
                                <h6 className="mb-0">Role Scores</h6>
                            </div>
                            <div className="card-body">
                                <p className="text-muted small mb-3">
                                    Bar chart presenting aggregated weekly sentiment scores for buyers, sellers, and dealers.
                                </p>
                                <div style={{ height: "250px" }}>
                                    <canvas ref={roleScoresChartRef}></canvas>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Role Outlook Table */}
                    {roleScores && (
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-secondary text-white">
                                <h5 className="mb-0">Role Outlook</h5>
                            </div>
                            <div className="card-body">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Role</th>
                                            <th>Score</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Buyer</strong></td>
                                            <td className={roleScores.buyer >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                                {roleScores.buyer?.toFixed(4)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Seller</strong></td>
                                            <td className={roleScores.seller >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                                {roleScores.seller?.toFixed(4)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Dealer</strong></td>
                                            <td className={roleScores.dealer >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                                {roleScores.dealer?.toFixed(4)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Top Weekly Topics Table */}
                    <div className="card mb-4 shadow-sm">
                        <div className="card-header bg-secondary text-white">
                            <h5 className="mb-0">Top Weekly Topics</h5>
                        </div>
                        <div className="card-body">
                            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                                <table className="table table-striped table-hover">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Topic</th>
                                            <th>Volume</th>
                                            <th>Score</th>
                                            <th>Intensity</th>
                                            <th>Consistency</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topicWeekly.length > 0 ? (
                                            topicWeekly.map((t, i) => (
                                                <tr key={i}>
                                                    <td><strong>{t.topic}</strong></td>
                                                    <td>{t.volume}</td>
                                                    <td className={t.topic_weekly_score >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                                        {t.topic_weekly_score?.toFixed(4)}
                                                    </td>
                                                    <td>{t.intensity?.toFixed(4)}</td>
                                                    <td>{t.consistency?.toFixed(4)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted">
                                                    No topics found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* LLM Summaries */}
                    {llmSummaries && (
                        <>
                            {llmSummaries.weekly_narrative && (
                                <div className="card mb-4 shadow-sm">
                                    <div className="card-header bg-info text-white">
                                        <h5 className="mb-0">Weekly Narrative</h5>
                                    </div>
                                    <div className="card-body">
                                        <p className="mb-0">{llmSummaries.weekly_narrative}</p>
                                    </div>
                                </div>
                            )}

                            {llmSummaries.topic_drivers?.length > 0 && (
                                <div className="card mb-4 shadow-sm">
                                    <div className="card-header bg-info text-white">
                                        <h5 className="mb-0">Topic Drivers</h5>
                                    </div>
                                    <div className="card-body">
                                        <ul className="mb-0">
                                            {llmSummaries.topic_drivers.map((driver, i) => (
                                                <li key={i}>{driver}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {llmSummaries.role_commentary && (
                                <div className="card mb-4 shadow-sm">
                                    <div className="card-header bg-info text-white">
                                        <h5 className="mb-0">Role Commentary</h5>
                                    </div>
                                    <div className="card-body">
                                        <p className="mb-0">{llmSummaries.role_commentary}</p>
                                    </div>
                                </div>
                            )}

                            {llmSummaries.market_outlook && (
                                <div className="card mb-4 shadow-sm">
                                    <div className="card-header bg-info text-white">
                                        <h5 className="mb-0">Market Outlook</h5>
                                    </div>
                                    <div className="card-body">
                                        <p className="mb-0">{llmSummaries.market_outlook}</p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Download Button */}
                    <div className="text-center mb-4">
                        <button
                            onClick={handleDownload}
                            className="btn btn-primary btn-lg"
                        >
                            📄 Download Full Report (PDF)
                        </button>
                    </div>

                    {/* Data Sources Footer */}
                    <div className="card shadow-sm">
                        <div className="card-body bg-light text-center">
                            <small className="text-muted">
                                <strong>Data Sources:</strong> BDSV (https://www.bdsv.org/) • Fastmarket (https://www.fastmarkets.com/)
                            </small>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default MarketMoodAnalyzer;
